/**
 * useDragSelect: press and hold on a row's checkbox cell, then drag across
 * the other rows to select the whole run (Gmail-style paint).
 *
 * - Paint mode is decided at the press: pressing on an unselected row
 *   selects the run, pressing on a selected row clears it.
 * - The run is EVERY row in `order` between the pressed row and the row now
 *   under the pointer, inclusive, so a fast drag that skips a row's
 *   `pointerenter` never leaves a gap. Moving back does not un-paint.
 * - Only the primary button starts a drag, and never with Shift held:
 *   shift-click stays with the selection hook (`useVariantSelection`).
 * - A press and release on the same row changes nothing here: the checkbox's
 *   own click toggles it, as before. After a drag that crossed rows, the one
 *   click the browser fires at the release is swallowed, so the release row
 *   is never toggled twice.
 * - Touch: a touch press captures the pointer to the pressed element, so
 *   other rows would never get `pointerenter`; the press releases that
 *   capture, and the handle cells carry `touch-action: none` so the finger
 *   paints instead of scrolling.
 *
 * Press state lives in refs; the selection is written only when the pointer
 * enters a new row (at most one state update per row crossed). No
 * `pointermove` handler.
 */
import { useCallback, useEffect, useRef, type CSSProperties, type MouseEvent, type PointerEvent } from 'react';

export interface UseDragSelectOptions {
  /** Row ids in display order. */
  order: readonly string[];
  isSelected: (id: string) => boolean;
  /** Select or clear many ids at once (`useVariantSelection.setSelected`). */
  setSelected: (ids: readonly string[], selected: boolean) => void;
}

export interface DragSelectHandleProps {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerEnter: (event: PointerEvent<HTMLElement>) => void;
  onClickCapture: (event: MouseEvent<HTMLElement>) => void;
  style: CSSProperties;
}

const HANDLE_STYLE: CSSProperties = { touchAction: 'none' };

export function useDragSelect({ order, isSelected, setSelected }: UseDragSelectOptions): {
  handleProps: (id: string) => DragSelectHandleProps;
} {
  const pressedRef = useRef<string | null>(null);
  const modeRef = useRef(true);
  const movedRef = useRef(false);
  const swallowClickRef = useRef(false);
  const endRef = useRef<(() => void) | null>(null);
  // The latest props, read from stable handlers.
  const latest = useRef({ order, setSelected });
  latest.current = { order, setSelected };

  const stopListening = useCallback(() => {
    if (endRef.current) {
      window.removeEventListener('pointerup', endRef.current);
      window.removeEventListener('pointercancel', endRef.current);
      endRef.current = null;
    }
  }, []);

  useEffect(() => stopListening, [stopListening]);

  const handleProps = useCallback(
    (id: string): DragSelectHandleProps => ({
      onPointerDown: (event) => {
        if (event.button !== 0 || event.shiftKey) return;
        pressedRef.current = id;
        modeRef.current = !isSelected(id);
        movedRef.current = false;
        // A touch press captures the pointer to the pressed element; release
        // it so the rows under the finger get pointerenter.
        const target = event.target as Element | null;
        for (const el of [target, event.currentTarget]) {
          if (el && typeof el.hasPointerCapture === 'function' && el.hasPointerCapture(event.pointerId)) {
            el.releasePointerCapture(event.pointerId);
          }
        }
        stopListening();
        const end = (): void => {
          stopListening();
          if (movedRef.current) {
            // Swallow the click the browser fires at the release, then forget
            // it if that click went to another element (it does when the
            // press and release rows differ).
            swallowClickRef.current = true;
            window.setTimeout(() => {
              swallowClickRef.current = false;
            }, 0);
          }
          pressedRef.current = null;
          movedRef.current = false;
        };
        endRef.current = end;
        window.addEventListener('pointerup', end);
        window.addEventListener('pointercancel', end);
      },
      onPointerEnter: (event) => {
        const pressed = pressedRef.current;
        if (pressed === null) return;
        // The release happened outside the window (Alt+Tab mid-drag): disarm,
        // so a plain hover never ticks rows.
        if (event.buttons === 0) {
          pressedRef.current = null;
          movedRef.current = false;
          return;
        }
        if (pressed === id) return;
        const { order: ids, setSelected: write } = latest.current;
        const from = ids.indexOf(pressed);
        const to = ids.indexOf(id);
        if (from === -1 || to === -1) return;
        movedRef.current = true;
        write(ids.slice(Math.min(from, to), Math.max(from, to) + 1), modeRef.current);
      },
      onClickCapture: (event) => {
        if (!swallowClickRef.current) return;
        swallowClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
      },
      style: HANDLE_STYLE,
    }),
    [isSelected, stopListening],
  );

  return { handleProps };
}
